import type { SanityTwoUp } from '#/lib/sanity/queries/twoUp';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';
import { FormattedText } from '#/components/FormattedText';
import { getHeadlineHighlightClass, Headline } from '#/components/Headline';
import { Media } from '#/components/Media';

import { VideoEmbed } from './VideoEmbed';

export function TwoUp(props: SanityTwoUp & PageLayoutSharedProps) {
  const hasFormattedBodyCopy = Boolean(props.formattedBodyCopy?.length);
  const isFirst = props.sectionIndex === 0;
  // A legacy background color takes precedence over the theme; without one,
  // the theme drives the section's colors via the shared surface map.
  const theme = props.background ? 'light' : props.theme;
  const isDark = isDarkSectionTheme(theme);
  const highlightClass = isDark
    ? getSectionThemeAccent(theme)
    : (getHeadlineHighlightClass({ background: props.background }) ??
      getSectionThemeAccent(theme));
  const eyebrowClass = highlightClass;

  return (
    <div
      className={cn('p-main', getSectionThemeSurface(theme), {
        'py-60 m:py-100': !isFirst,
        'mt-[calc(var(--header-height)*-1)] pb-60 m:pb-100 pt-[calc(var(--header-height)+5rem)]':
          isFirst,
      })}
      style={{ background: props.background ?? undefined }}
    >
      <div className="mx-auto grid max-w-1235 grid-cols-8 items-center gap-x-20">
        <div
          className={cn(
            'two-up-content col-span-full m:col-span-4 m:row-start-1 l:col-span-3',
            {
              'm:col-start-1': props.desktopLayout === 'textImage',
              'm:col-start-5 l:col-start-6':
                props.desktopLayout === 'imageText',
              'row-start-2 mt-30 m:mt-0': props.mobileLayout === 'imageText',
              'mb-30 m:mb-0': props.mobileLayout === 'textImage',
            },
          )}
        >
          {props.useLogoAsOverline &&
          props.overlineLogo?.url &&
          props.overlineLogoWidth ? (
            <Media
              media={props.overlineLogo}
              style={{ width: props.overlineLogoWidth }}
              className="mb-12 overflow-clip m:mb-20"
              sizes={`${props.overlineLogoWidth}px`}
            />
          ) : null}
          <Headline
            value={props.headline}
            overline={props.useLogoAsOverline ? undefined : props.overline}
            overlineClassName={cn('type-eyebrow', eyebrowClass)}
            variant={5}
            highlightClass={highlightClass}
            className="mb-10 m:mb-25"
            dataSanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'headline',
            )}
          />
          {hasFormattedBodyCopy ? (
            <FormattedText
              value={props.formattedBodyCopy ?? []}
              className={isDark ? 'text-body-cloud' : 'text-body'}
              dataSanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'formattedBodyCopy',
              )}
            />
          ) : (
            <p
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'bodyCopy',
              )}
              className={isDark ? 'text-body-cloud' : 'text-body'}
            >
              {props.bodyCopy}
            </p>
          )}
          {props.cta.href ? (
            <Button
              {...props.cta}
              theme={getSectionButtonTheme(theme)}
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'cta',
              )}
              className="mt-25"
            >
              {props.cta.title}
            </Button>
          ) : null}
        </div>
        <div
          className={cn(
            'two-up-media col-span-full m:col-span-4 m:row-start-1 l:col-span-5',
            {
              'm:col-start-1 m:pr-25 l:pr-60':
                props.desktopLayout === 'imageText',
              'm:col-start-5 m:pl-35 l:col-start-4 l:pl-60':
                props.desktopLayout === 'textImage',
            },
          )}
        >
          {props.media._type === 'videoEmbed' ? (
            <VideoEmbed {...props.media} className="my-0" />
          ) : (
            <Media
              media={props.media}
              sizes={['100vw', { m: '60vw', l: '1200px' }]}
            />
          )}
        </div>
        {props.details && props.details?.length > 0 ? (
          <div className="col-span-full mt-50 flex flex-col gap-30 m:flex-row l:mt-70">
            {props.details.map((detail, i) => (
              <div
                key={detail._key}
                data-sanity={createSanityFieldDataAttribute(
                  props.sanitySource,
                  `details[_key=="${detail._key}"]`,
                )}
                className="two-up-detail flex-1"
                style={{ transitionDelay: `${0.4 + i * 0.1}s` }}
              >
                <div
                  className={cn(
                    'max-w-[375px] text-pretty',
                    isDark ? 'text-body-cloud' : 'text-body',
                  )}
                >
                  <h3
                    className={cn(
                      'mb-10 !font-[440]',
                      isDark ? '!text-cloud' : '!text-charcoal',
                    )}
                  >
                    {detail.title}
                  </h3>
                  <p>{detail.caption}</p>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
